import tenantConstants from '@constants';
import Moengage from '@moengage/web-sdk';
import { isProduction } from '../../utility/env';

const MAX_PENDING_EVENTS = 50;
const MAX_SDK_QUEUE_LENGTH = 100;

let pendingEvents = [];

const initializeUserAttributes = (user) => {
  Moengage.add_unique_user_id(user?.external_id);
  Moengage.add_email(user?.email);
  Moengage.add_user_name(user?.name);
};

const isNil = (value) => value === undefined || value === null;

const sendMoengageEvent = (eventName, attributes) => {
  // SDK never loaded (e.g. blocked by an ad-blocker) — stop growing its stub queue
  if (window.moengage_q?.length > MAX_SDK_QUEUE_LENGTH) return;
  try {
    Moengage.track_event(eventName, attributes)?.catch?.(() => {});
  } catch (e) {
    // fail silently, no retry
  }
};

const flushPendingMoengageEvents = () => {
  const events = pendingEvents;
  pendingEvents = [];
  events.forEach(({ eventName, attributes }) => sendMoengageEvent(eventName, attributes));
};

export function initializeMoEngage(user) {
  Moengage?.initialize({
    app_id: process.env.REACT_APP_MOENGAGE_APP_ID,
    debug_logs: isProduction ? 0 : 1,
    cluster: 'DC_1',
    swPath: '/moengage-service-worker.js',
  });

  window.moengageInitialized = true; // Set flag on global window object

  initializeUserAttributes(user);

  // Ensure service worker is registered only once
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration) {
        navigator.serviceWorker
          .register('/moengage-service-worker.js')
          .catch((err) => console.error('Service Worker registration failed:', err));
      }
    });
  }

  tenantConstants.MOENGAGE_EVENTS_ENABLED && flushPendingMoengageEvents();
}

export function setMoengageUserAttributes(user, locale) {
  if (!tenantConstants.MOENGAGE_EVENTS_ENABLED || !window.moengageInitialized) return;
  const attributes = {
    agency_id: user?.agency?.id,
    agency_name: user?.agency?.name,
    is_agency: !!user?.agency,
    package_type: user?.package?.slug,
    current_package_duration: user?.package?.duration_in_months,
    language: locale,
  };
  Object.entries(attributes).forEach(([name, value]) => {
    if (isNil(value)) return;
    try {
      Moengage.add_user_attribute(name, value)?.catch?.(() => {});
    } catch (e) {
      // fail silently, no retry
    }
  });
}

export function trackMoengageEvent(eventName, attributes = {}) {
  if (!tenantConstants.MOENGAGE_EVENTS_ENABLED) return;
  const flatAttributes = Object.fromEntries(Object.entries({ ...attributes }).filter(([, value]) => !isNil(value)));
  if (window.moengageInitialized !== true) {
    pendingEvents.push({ eventName, attributes: flatAttributes });
    if (pendingEvents.length > MAX_PENDING_EVENTS) pendingEvents.shift();
    return;
  }
  sendMoengageEvent(eventName, flatAttributes);
}

export function logoutMoengage() {
  pendingEvents = [];
  Moengage?.destroy_session();
}
